import { z } from "zod";

// Define scoring weights for different assessment categories
export const CATEGORY_WEIGHTS = {
  education: 0.25,
  experience: 0.25,
  skills: 0.20,
  achievements: 0.15,
  recognition: 0.15
} as const;

// Validation schema for assessment scores
export const ScoreSchema = z.object({
  education: z.number().min(0).max(100),
  experience: z.number().min(0).max(100),
  skills: z.number().min(0).max(100),
  achievements: z.number().min(0).max(100),
  recognition: z.number().min(0).max(100)
});

export type AssessmentScores = z.infer<typeof ScoreSchema>;

// Calculate success likelihood based on scores and pathway requirements
export const calculateSuccessLikelihood = (
  scores: AssessmentScores,
  pathwayRequirements: Record<keyof AssessmentScores, number>,
  additionalFactors: {
    industryDemand?: number; // 0-100
    competitionLevel?: number; // 0-100
    processingTimes?: number; // Average in days
  } = {}
): number => {
  // Base likelihood from scores
  let baseLikelihood = Object.entries(scores).reduce((total, [category, score]) => {
    const weight = CATEGORY_WEIGHTS[category as keyof typeof CATEGORY_WEIGHTS];
    const requirement = pathwayRequirements[category as keyof AssessmentScores];
    const match = Math.min((score / requirement) * 100, 100);
    return total + (match * weight);
  }, 0);

  // Adjust for additional factors
  if (additionalFactors.industryDemand) {
    baseLikelihood *= (1 + (additionalFactors.industryDemand - 50) / 200);
  }
  
  if (additionalFactors.competitionLevel) {
    baseLikelihood *= (1 - (additionalFactors.competitionLevel - 50) / 200);
  }

  // Ensure final likelihood is between 0-100
  return Math.min(Math.max(Math.round(baseLikelihood), 0), 100);
};

// Get likelihood status and description
export const getLikelihoodStatus = (likelihood: number): {
  status: 'high' | 'medium' | 'low';
  color: string;
  description: string;
} => {
  if (likelihood >= 75) {
    return {
      status: 'high',
      color: 'text-green-500',
      description: 'Strong likelihood of success. Your profile strongly matches the requirements.'
    };
  }
  if (likelihood >= 50) {
    return {
      status: 'medium',
      color: 'text-yellow-500',
      description: 'Moderate likelihood of success. Consider strengthening key areas.'
    };
  }
  return {
    status: 'low',
    color: 'text-red-500',
    description: 'Lower likelihood of success. Significant improvements needed.'
  };
};

// Generate recommendations based on scores and success likelihood
export const generateRecommendations = (
  scores: AssessmentScores,
  likelihood: number
): string[] => {
  const recommendations: string[] = [];

  // Add likelihood-based recommendations
  if (likelihood < 75) {
    recommendations.push("Consider strengthening your application before proceeding");
  }

  // Add category-specific recommendations
  Object.entries(scores).forEach(([category, score]) => {
    if (score < 75) {
      switch (category) {
        case 'education':
          recommendations.push("Consider pursuing additional qualifications or certifications");
          break;
        case 'experience':
          recommendations.push("Gain more relevant industry experience");
          break;
        case 'skills':
          recommendations.push("Develop additional technical or professional skills");
          break;
        case 'achievements':
          recommendations.push("Focus on achieving measurable results in your field");
          break;
        case 'recognition':
          recommendations.push("Seek professional recognition through awards or publications");
          break;
      }
    }
  });

  return recommendations;
};

// Calculate weighted score for a category
export const calculateWeightedScore = (score: number, category: keyof typeof CATEGORY_WEIGHTS) => {
  return score * CATEGORY_WEIGHTS[category];
};

// Calculate overall score from category scores
export const calculateOverallScore = (scores: AssessmentScores): number => {
  return Object.entries(scores).reduce((total, [category, score]) => {
    return total + calculateWeightedScore(score, category as keyof typeof CATEGORY_WEIGHTS);
  }, 0);
};

// Get score status (color coding)
export const getScoreStatus = (score: number): 'success' | 'warning' | 'error' => {
  if (score >= 75) return 'success';
  if (score >= 50) return 'warning';
  return 'error';
};